import { useEffect, useMemo, useState } from "react";
import {
  clearAdminToken,
  getAdminDashboardData,
  overrideJobAssignment,
  setAdminToken,
  updateJobStatus,
} from "../utils/googleSheet";
import "./AdminDashboard.css";

const STATUS_FILTERS = ["All", "Waiting", "Assigned", "Pending", "Completed"];
const STATUS_ACTIONS = ["Assigned", "Pending", "Completed"];
const ADMIN_PANEL_PASSWORD = (process.env.REACT_APP_ADMIN_PANEL_PASSWORD || "").trim();
const ADMIN_SESSION_KEY = "maskanh_admin_session";

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [error, setError] = useState("");
  const [busyJobId, setBusyJobId] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(ADMIN_SESSION_KEY) === "true";
  });

  const loadDashboard = async () => {
    try {
      setError("");
      const data = await getAdminDashboardData();
      setDashboard(data);
    } catch (err) {
      setError(err.message || "Failed to load admin panel.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Maskanh Admin Panel";
    if (!isAuthorized) {
      setLoading(false);
      return undefined;
    }

    loadDashboard();

    const interval = setInterval(() => {
      loadDashboard();
    }, 20000);

    return () => clearInterval(interval);
  }, [isAuthorized]);

  const handleLogin = (e) => {
    e.preventDefault();

    if (!ADMIN_PANEL_PASSWORD) {
      setError("Admin password is missing in .env. Add REACT_APP_ADMIN_PANEL_PASSWORD.");
      return;
    }

    if (password.trim() !== ADMIN_PANEL_PASSWORD) {
      setError("Invalid admin password.");
      return;
    }

    setError("");
    setAdminToken(password.trim());
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ADMIN_SESSION_KEY, "true");
    }
    setIsAuthorized(true);
  };

  const handleLogout = () => {
    clearAdminToken();
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(ADMIN_SESSION_KEY);
    }
    setDashboard(null);
    setPassword("");
    setIsAuthorized(false);
  };

  const filteredJobs = useMemo(() => {
    if (!dashboard?.jobs) return [];
    if (statusFilter === "All") return dashboard.jobs;
    return dashboard.jobs.filter((job) => job.Status === statusFilter);
  }, [dashboard, statusFilter]);

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      setBusyJobId(jobId);
      await updateJobStatus({ jobId, status: newStatus });
      await loadDashboard();
    } catch (err) {
      setError(err.message || "Unable to update status.");
    } finally {
      setBusyJobId("");
    }
  };

  const handleAssignmentChange = async (jobId, technicianId) => {
    try {
      setBusyJobId(jobId);
      await overrideJobAssignment({ jobId, technicianId });
      await loadDashboard();
    } catch (err) {
      setError(err.message || "Unable to update assignment.");
    } finally {
      setBusyJobId("");
    }
  };

  return (
    <main className="admin-page">
      <div className="container">
        <header className="admin-header">
          <h1>Maskanh Admin Panel</h1>
          <p>Live dispatch board with auto-assignment and status tracking.</p>
        </header>

        {error && <div className="admin-error">{error}</div>}

        {!isAuthorized ? (
          <section className="admin-login-card">
            <h2>Admin Login Required</h2>
            <p>Only admins can access this dispatch dashboard.</p>
            <form onSubmit={handleLogin} className="admin-login-form">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoComplete="current-password"
                required
              />
              <button type="submit" className="btn btn-primary">
                Unlock Admin Panel
              </button>
            </form>
          </section>
        ) : loading ? (
          <p className="admin-loading">Loading dashboard...</p>
        ) : (
          <>
            <div className="admin-toolbar">
              <button type="button" className="btn btn-outline" onClick={handleLogout}>
                Logout
              </button>
            </div>

            <section className="admin-stats-grid">
              <article className="admin-stat-card">
                <span>New / waiting</span>
                <strong>{dashboard?.stats?.waiting ?? 0}</strong>
              </article>
              <article className="admin-stat-card">
                <span>Assigned</span>
                <strong>{dashboard?.stats?.assigned ?? 0}</strong>
              </article>
              <article className="admin-stat-card">
                <span>Pending</span>
                <strong>{dashboard?.stats?.pending ?? 0}</strong>
              </article>
              <article className="admin-stat-card">
                <span>Completed</span>
                <strong>{dashboard?.stats?.completed ?? 0}</strong>
              </article>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-head">
                <h2>Job requests</h2>
                <div className="admin-filters">
                  {STATUS_FILTERS.map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      className={statusFilter === filter ? "active" : ""}
                      onClick={() => setStatusFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Location</th>
                      <th>Problem</th>
                      <th>Time slot</th>
                      <th>Auto-assign tech</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map((job) => {
                      const jobId = job["Job ID"];
                      const currentTechId = job["Assigned Technician ID"] || "";
                      const availableTechs = dashboard.technicians || [];

                      return (
                        <tr key={jobId}>
                          <td>{jobId}</td>
                          <td>
                            <div>{job.Name || "-"}</div>
                            <small>{job["Property Type"] || "-"}</small>
                          </td>
                          <td>{job.Location || "-"}</td>
                          <td>{job.Problem || "-"}</td>
                          <td>{job["Preferred DateTime"] || "-"}</td>
                          <td>
                            <select
                              value={currentTechId}
                              disabled={busyJobId === jobId}
                              onChange={(e) =>
                                handleAssignmentChange(jobId, e.target.value)
                              }
                            >
                              <option value="">Unassigned</option>
                              {availableTechs.map((tech) => {
                                const techName =
                                  tech.Name ||
                                  tech["Technician Name"] ||
                                  tech["Tech Name"] ||
                                  "Technician";
                                return (
                                  <option key={tech["Tech ID"]} value={tech["Tech ID"]}>
                                    {techName} ({tech.Area || "No area"})
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td>
                            <span className={`status-badge ${job.Status?.toLowerCase()}`}>
                              {job.Status || "-"}
                            </span>
                          </td>
                          <td>
                            <select
                              value={job.Status || "Waiting"}
                              disabled={busyJobId === jobId}
                              onChange={(e) =>
                                handleStatusChange(jobId, e.target.value)
                              }
                            >
                              {STATUS_ACTIONS.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                              <option value="Waiting">Waiting</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-head">
                <h2>Technicians - availability</h2>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Tech ID</th>
                      <th>Name</th>
                      <th>Area</th>
                      <th>Skill</th>
                      <th>Shift</th>
                      <th>Availability</th>
                      <th>Jobs done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard?.technicians || []).map((tech) => (
                      <tr key={tech["Tech ID"]}>
                        <td>{tech["Tech ID"]}</td>
                        <td>{tech.Name || tech["Technician Name"] || "-"}</td>
                        <td>{tech.Area}</td>
                        <td>{tech.Skill}</td>
                        <td>{tech.Shift}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              (tech.Availability || "").toLowerCase() === "available"
                                ? "available"
                                : "busy"
                            }`}
                          >
                            {tech.Availability || "-"}
                          </span>
                        </td>
                        <td>{tech["Jobs done"] ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
